import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { taskService, userService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  CheckSquare,
  Search,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  SlidersHorizontal,
  AlertTriangle,
  Download,
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const DEFAULT_COLUMNS = {
  title: true,
  case_number: true,
  case_title: true,
  assignee: true,
  priority: true,
  task_type: true,
  due_date: true,
  estimated_hours: true,
};

const COLUMN_DEFS = [
  { key: 'title', label: 'Task', sortable: true, alwaysVisible: true },
  { key: 'case_number', label: 'Case #', sortable: true },
  { key: 'case_title', label: 'Case Name', sortable: false },
  { key: 'assignee', label: 'Assigned To', sortable: true },
  { key: 'priority', label: 'Priority', sortable: true },
  { key: 'task_type', label: 'Type', sortable: true },
  { key: 'due_date', label: 'Due Date', sortable: true },
  { key: 'estimated_hours', label: 'Est. Hours', sortable: false },
];

const STATUS_OPTIONS = [
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: '', label: 'All' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const SORT_KEY_MAP = {
  title: 'title',
  case_number: 'case_number',
  assignee: 'assigned_to',
  priority: 'priority',
  task_type: 'task_type',
  due_date: 'due_date',
};

const SortIcon = ({ columnKey, sortBy, sortDirection }) => {
  const mapped = SORT_KEY_MAP[columnKey] || columnKey;
  if (sortBy === mapped) {
    return sortDirection === 'asc'
      ? <ChevronUp className="inline h-4 w-4 ml-1" />
      : <ChevronDown className="inline h-4 w-4 ml-1" />;
  }
  return <ChevronsUpDown className="inline h-4 w-4 ml-1 text-gray-300" />;
};

const Tasks = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('todo');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [attorneyFilter, setAttorneyFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchDebounceRef = useRef(null);
  const [sortBy, setSortBy] = useState('due_date');
  const [sortDirection, setSortDirection] = useState('asc');
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_COLUMNS);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const columnSettingsRef = useRef(null);
  const [attorneys, setAttorneys] = useState([]);

  useEffect(() => {
    if (!isAuthenticated || authLoading) return;
    const loadDropdowns = async () => {
      try {
        const usersRes = await userService.getUsers();
        const activeUsers = (Array.isArray(usersRes.data) ? usersRes.data : []).filter(u => u.firm_active !== false && !u.has_pending_invitation);
        setAttorneys(activeUsers);
      } catch (err) {
        console.error('Error loading dropdown data:', err);
      }
    };
    loadDropdowns();
  }, [isAuthenticated, authLoading]);

  const hasFetchedOnce = useRef(false);
  const fetchIdRef = useRef(0);

  useEffect(() => {
    if (!isAuthenticated || authLoading) return;
    const fetchId = ++fetchIdRef.current;
    if (hasFetchedOnce.current) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    const params = { sort_by: sortBy, sort_direction: sortDirection, skip: 0, limit: 100 };
    if (statusFilter) params.status_filter = statusFilter;
    if (priorityFilter) params.priority_filter = priorityFilter;
    if (attorneyFilter) params.attorney_id = attorneyFilter;
    if (debouncedSearch) params.search = debouncedSearch;
    taskService.getTasksTableView(params)
      .then(res => {
        if (fetchId !== fetchIdRef.current) return;
        setTasks(res.data.tasks || []);
        setTotalCount(res.data.total_count || 0);
        hasFetchedOnce.current = true;
      })
      .catch(err => {
        if (fetchId !== fetchIdRef.current) return;
        console.error('Error fetching tasks:', err);
        setTasks([]);
        setTotalCount(0);
      })
      .finally(() => {
        if (fetchId !== fetchIdRef.current) return;
        setLoading(false);
        setRefreshing(false);
      });
  }, [isAuthenticated, authLoading, sortBy, sortDirection, statusFilter, priorityFilter, attorneyFilter, debouncedSearch]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(val);
    }, 300);
  };

  const handleSort = useCallback((columnKey) => {
    const col = COLUMN_DEFS.find(c => c.key === columnKey);
    if (!col || !col.sortable) return;
    const newSortBy = SORT_KEY_MAP[columnKey] || columnKey;
    let newDir = 'asc';
    if (sortBy === newSortBy) {
      newDir = sortDirection === 'asc' ? 'desc' : 'asc';
    }
    setSortBy(newSortBy);
    setSortDirection(newDir);
  }, [sortBy, sortDirection]);

  const toggleColumn = (key) => {
    const col = COLUMN_DEFS.find(c => c.key === key);
    if (col?.alwaysVisible) return;
    setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (columnSettingsRef.current && !columnSettingsRef.current.contains(e.target)) {
        setShowColumnSettings(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDueDateClasses = (dueDateStr) => {
    if (!dueDateStr) return 'text-gray-500';
    const due = new Date(dueDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDay = new Date(due);
    dueDay.setHours(0, 0, 0, 0);
    if (dueDay < today) return 'text-red-600 font-medium';
    if (dueDay.getTime() === today.getTime()) return 'text-amber-600 font-medium';
    return 'text-gray-600';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getStatusColor = (s) => {
    switch (s) {
      case 'todo': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (p) => {
    switch (p) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTaskType = (type) => {
    if (!type) return '-';
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatStatus = (s) => {
    if (!s) return '-';
    if (s === 'in_progress') return 'In Progress';
    return s.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Export currently filtered task list to PDF
  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });

    // Title
    const activeStatus = STATUS_OPTIONS.find(o => o.value === statusFilter);
    const statusLabel = activeStatus ? activeStatus.label : 'All';
    doc.setFontSize(16);
    doc.text(`Tasks — ${statusLabel}`, 14, 18);

    // Subtitle with applied filters
    const filterParts = [];
    if (attorneyFilter) {
      const att = attorneys.find(a => a.id === attorneyFilter);
      if (att) filterParts.push(`Attorney: ${att.first_name} ${att.last_name}`);
    }
    if (priorityFilter) {
      const pri = PRIORITY_OPTIONS.find(p => p.value === priorityFilter);
      if (pri) filterParts.push(`Priority: ${pri.label}`);
    }
    if (debouncedSearch) filterParts.push(`Search: "${debouncedSearch}"`);
    if (filterParts.length > 0) {
      doc.setFontSize(10);
      doc.text(filterParts.join('  |  '), 14, 25);
    }

    doc.setFontSize(8);
    doc.text(`Generated ${new Date().toLocaleString()}  —  ${tasks.length} task${tasks.length !== 1 ? 's' : ''}`, 14, filterParts.length > 0 ? 30 : 25);

    // Build columns from visible columns + status (always shown)
    const visibleCols = COLUMN_DEFS.filter(c => visibleColumns[c.key]);
    const pdfColumns = [...visibleCols.map(c => c.label), 'Status'];

    // Build rows from currently loaded tasks
    const getCellText = (task, colKey) => {
      switch (colKey) {
        case 'title': return task.title || '-';
        case 'case_number': return task.case_number || '-';
        case 'case_title': return task.case_title || '-';
        case 'assignee': return task.assignee_name || '-';
        case 'priority': return task.priority || '-';
        case 'task_type': return formatTaskType(task.task_type);
        case 'due_date': return task.due_date ? new Date(task.due_date).toLocaleDateString() : '-';
        case 'estimated_hours': return task.estimated_hours != null ? `${task.estimated_hours}h` : '-';
        default: return '-';
      }
    };

    const pdfRows = tasks.map(t => [
      ...visibleCols.map(col => getCellText(t, col.key)),
      formatStatus(t.status),
    ]);

    autoTable(doc, {
      head: [pdfColumns],
      body: pdfRows,
      startY: filterParts.length > 0 ? 34 : 29,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [234, 179, 8], textColor: [0, 0, 0], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [249, 250, 251] },
    });

    doc.save(`tasks-${statusLabel.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const renderCell = (task, colKey) => {
    switch (colKey) {
      case 'title':
        return (
          <span className="text-sm font-medium text-gray-900 inline-flex items-center gap-1">
            {task.title}
            {task.court_deadline && (
              <AlertTriangle className="w-3 h-3 text-red-500" title="Court deadline" />
            )}
          </span>
        );
      case 'case_number':
        return <span className="text-sm text-gray-600">{task.case_number || '-'}</span>;
      case 'case_title':
        return <span className="text-sm text-gray-600">{task.case_title || '-'}</span>;
      case 'assignee':
        return <span className="text-sm text-gray-600">{task.assignee_name || '-'}</span>;
      case 'priority':
        return (
          <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(task.priority)}`}>
            {task.priority}
          </span>
        );
      case 'task_type':
        return <span className="text-sm text-gray-600">{formatTaskType(task.task_type)}</span>;
      case 'due_date':
        return (
          <span className={`text-sm ${getDueDateClasses(task.due_date)}`}>
            {formatDate(task.due_date)}
          </span>
        );
      case 'estimated_hours':
        return <span className="text-sm text-gray-600">{task.estimated_hours != null ? `${task.estimated_hours}h` : '-'}</span>;
      default:
        return '-';
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated && !authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-600">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  const activeColumns = COLUMN_DEFS.filter(c => visibleColumns[c.key]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <CheckSquare className="mr-3 h-8 w-8" />
            Tasks
          </h1>
          <p className="text-gray-600">
            {totalCount} task{totalCount !== 1 ? 's' : ''} found
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportPDF}
            disabled={tasks.length === 0}
            className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg flex items-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Export filtered tasks to PDF"
          >
            <Download className="mr-2 h-4 w-4" />
            Export PDF
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center space-x-1">
            {STATUS_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                className={`px-3 py-1.5 text-sm rounded-full font-medium transition-colors ${
                  statusFilter === opt.value
                    ? 'bg-green-100 text-green-800 ring-1 ring-green-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="h-8 w-px bg-gray-200"></div>

          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          >
            {PRIORITY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <select
            value={attorneyFilter}
            onChange={(e) => setAttorneyFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          >
            <option value="">All Attorneys</option>
            {attorneys.map(a => (
              <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>
            ))}
          </select>

          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search tasks, cases..."
              value={searchText}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            />
          </div>

          <div className="relative" ref={columnSettingsRef}>
            <button
              onClick={() => setShowColumnSettings(!showColumnSettings)}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-500"
              title="Column settings"
            >
              <SlidersHorizontal className="h-4 w-4" />
            </button>
            {showColumnSettings && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-20 p-3">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Visible Columns</h4>
                {COLUMN_DEFS.map(col => (
                  <label
                    key={col.key}
                    className={`flex items-center space-x-2 py-1.5 px-1 rounded text-sm ${
                      col.alwaysVisible ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 cursor-pointer hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={visibleColumns[col.key]}
                      disabled={col.alwaysVisible}
                      onChange={() => toggleColumn(col.key)}
                      className="rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
                    />
                    <span>{col.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {activeColumns.map(col => (
                  <th
                    key={col.key}
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      col.sortable ? 'cursor-pointer select-none hover:bg-gray-100' : ''
                    }`}
                    onClick={() => col.sortable && handleSort(col.key)}
                  >
                    {col.label}
                    {col.sortable && <SortIcon columnKey={col.key} sortBy={sortBy} sortDirection={sortDirection} />}
                  </th>
                ))}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className={`bg-white divide-y divide-gray-200 transition-opacity duration-200 ${refreshing ? 'opacity-50' : 'opacity-100'}`}>
              {loading ? (
                <tr>
                  <td colSpan={activeColumns.length + 1} className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto mb-2"></div>
                    <p className="text-gray-500">Loading tasks...</p>
                  </td>
                </tr>
              ) : tasks.length > 0 ? (
                tasks.map(task => (
                  <tr
                    key={task.id}
                    onClick={() => navigate(`/tasks/${task.id}`)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    {activeColumns.map(col => (
                      <td key={col.key} className="px-6 py-4 whitespace-nowrap">
                        {renderCell(task, col.key)}
                      </td>
                    ))}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(task.status)}`}>
                        {task.status === 'in_progress' ? 'In Progress' : task.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={activeColumns.length + 1} className="px-6 py-12 text-center">
                    <CheckSquare className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-500 font-medium">No tasks match your filters</p>
                    <p className="text-sm text-gray-400 mt-1">Try adjusting your search or filter criteria</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Tasks;
